import React, { FunctionComponent, memo } from 'react'
import FontelloIcon from '@sport1/react-elements/FontelloIcon'
import Grid from '@sport1/react-elements/Grid'
import PlatformHelper from '@sport1/react-elements/PlatformHelper'
import Touchable from '@sport1/react-elements/Touchable'
import BodyFont from '../../Theme/Fonts/Body'
import SmallBodyFont from '../../Theme/Fonts/SmallBody'
import VideoIndicator, {
    VideoIndicatorProps,
} from '../../BaseComponents/ProgressBar/VideoIndicator'
import CaptionFont from '../../Theme/Fonts/Caption'
import NavigationBar from '../../Navigation/NavigationBar'
import { AudioPlayerProps } from '../AudioPlayerProps'
import { TestID } from '../../utils/PropTypesDefinitions'
import BigAudioPlayerImage from './Image'

export type BigAudioPlayerProps = VideoIndicatorProps &
    AudioPlayerProps &
    TestID & {
        /** the width of the player
         *
         * @defaultValue: screen width
         */
        width?: number
        /** the height of the player
         *
         * @defaultValue: screen height
         */
        height?: number
        /** the headline text */
        headlineText: string
        /** the current time of the audio formatted as string */
        formattedCurrentTime: string
        /** the duration of the audio formatted as string */
        formattedDuration: string
        /** the callback when the user presses on the forward icon
         *
         * @TJS-type function
         * */
        onForward: () => void
        /** callback function when the user presses on the image
         *
         * @TJS-type function
         */
        onImagePress?: () => void
    }

const IMAGE_PADDING = 16
const HEIGHT_WITHOUT_IMAGE = 305

const { width: WIDTH, height: HEIGHT } = PlatformHelper.getDimensions()
const SCREEN_WIDTH = Math.min(WIDTH, HEIGHT)
const SCREEN_HEIGHT = Math.max(WIDTH, HEIGHT)

const BigAudioPlayer: FunctionComponent<BigAudioPlayerProps> = ({
    /* AudioPlayerProps */
    paused = true,
    title,
    episode,
    imageUrl,
    alt,
    onClose,
    onPlayerStateChange,
    onForward,
    onBackward,
    /* VideoIndicatorProps */
    enableIndicatorProgress,
    progressPercent,
    bufferingPercent,
    duration,
    onSeekStart,
    onSeekEnd,
    /* BigAudioPlayerProps */
    width,
    height,
    headlineText,
    formattedCurrentTime,
    formattedDuration,
    onImagePress,
    /* TestID Props */
    testID,
}) => {
    const contentHeight = height || SCREEN_HEIGHT
    const imageWidth =
        Math.min(width || SCREEN_WIDTH, contentHeight - HEIGHT_WITHOUT_IMAGE) - IMAGE_PADDING * 2
    return (
        <Grid noExpand paddingX={5} backgroundColor="BLACK" testID={testID} height={contentHeight}>
            <NavigationBar
                mainComponent={headlineText}
                leftIcon={{
                    name: 'scroll-down',
                    color: 'pearl',
                    onPress: onClose,
                    size: 11,
                }}
            />
            <Grid noExpand centerVertical height={contentHeight - 44}>
                <Grid noExpand center marginY={imageWidth <= HEIGHT_WITHOUT_IMAGE ? 5 : 7}>
                    <Touchable onPress={onImagePress}>
                        <BigAudioPlayerImage imageUrl={imageUrl} alt={alt} width={imageWidth} />
                    </Touchable>
                </Grid>
                <Grid noExpand marginBottom={3}>
                    <BodyFont color="WHITE" bold numberOfLines={2}>
                        {title}
                    </BodyFont>
                </Grid>
                <SmallBodyFont color="keshi-3" numberOfLines={1}>
                    {episode}
                </SmallBodyFont>
                <VideoIndicator
                    enableIndicatorProgress={enableIndicatorProgress}
                    progressPercent={progressPercent}
                    bufferingPercent={bufferingPercent}
                    duration={duration}
                    onSeekStart={onSeekStart}
                    onSeekEnd={onSeekEnd}
                />
                <Grid noExpand horizontal evenly marginBottom={7}>
                    <CaptionFont color="WHITE" bold>
                        {formattedCurrentTime}
                    </CaptionFont>
                    <CaptionFont color="WHITE" bold>
                        {formattedDuration}
                    </CaptionFont>
                </Grid>
                <Grid noExpand horizontal center>
                    <Touchable onPress={onBackward}>
                        <Grid noExpand paddingX={5} paddingY={4}>
                            <FontelloIcon name="replay-15" color="WHITE" fontSize={24} />
                        </Grid>
                    </Touchable>
                    <Touchable onPress={onPlayerStateChange}>
                        <Grid noExpand center paddingX={6} height={50}>
                            <FontelloIcon
                                name={paused ? 'play-active' : 'pause-active'}
                                color="WHITE"
                                fontSize={40}
                            />
                        </Grid>
                    </Touchable>
                    <Touchable onPress={onForward}>
                        <Grid noExpand paddingX={5} paddingY={4}>
                            <FontelloIcon name="forward-15" color="WHITE" fontSize={24} />
                        </Grid>
                    </Touchable>
                </Grid>
            </Grid>
        </Grid>
    )
}

export default memo(BigAudioPlayer)
